// indexedDB.js
// This file handles the IndexedDB implementation for web

// Initialize IndexedDB
export const initIndexedDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('ApiResponsesDB', 1);
      
      request.onerror = (event) => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores (tables) if they don't exist
        const tables = ['scan_barcode', 'users', 'orders', 'products', 'settings'];
        
        tables.forEach(tableName => {
          if (!db.objectStoreNames.contains(tableName)) {
            db.createObjectStore(tableName, { keyPath: 'id' });
          }
        });
      };
      
      request.onsuccess = (event) => {
        const idb = event.target.result;
        console.log('IndexedDB initialized successfully');
        resolve(idb);
      };
    });
  };
  
  // Store for the IndexedDB instance
  let idbInstance = null;
  
  // Get the IndexedDB instance, initializing if necessary
  export const getIndexedDBInstance = async () => {
    if (!idbInstance) {
      idbInstance = await initIndexedDB();
    }
    return idbInstance;
  };
  
  // IndexedDB implementation of SQL-like transaction interface
  export const createIndexedDBInterface = () => {
    return {
      transaction: async (callback, errorCallback, successCallback) => {
        try {
          // Make sure IndexedDB is initialized
          const idbInstance = await getIndexedDBInstance();
          
          const tx = {
            executeSql: async (query, params, successFn, errorFn) => {
              try {
                // Very basic SQL parsing to determine operation and table
                const operation = query.toLowerCase().includes('create table') ? 'create' :
                               query.toLowerCase().includes('insert') ? 'insert' :
                               query.toLowerCase().includes('select') ? 'select' : 'unknown';
                
                // Extract table name (very basic parsing)
                let tableName = '';
                if (query.toLowerCase().includes('scan_barcode')) tableName = 'scan_barcode';
                else if (query.toLowerCase().includes('users')) tableName = 'users';
                else if (query.toLowerCase().includes('orders')) tableName = 'orders';
                else if (query.toLowerCase().includes('products')) tableName = 'products';
                else if (query.toLowerCase().includes('settings')) tableName = 'settings';
                
                if (operation === 'create') {
                  // CREATE TABLE - just return success for web
                  successFn && successFn(tx, { rows: { _array: [] } });
                } 
                else if (operation === 'insert') {
                  // INSERT operation
                  const id = params[0] || Date.now();
                  const dataToStore = {
                    id: id,
                    data: params[1],
                    timestamp: params[2] || Date.now()
                  };
                  
                  const transaction = idbInstance.transaction([tableName], 'readwrite');
                  const objectStore = transaction.objectStore(tableName);
                  
                  return new Promise((resolve, reject) => {
                    const request = objectStore.put(dataToStore);
                    
                    request.onsuccess = () => {
                      successFn && successFn(tx, { rowsAffected: 1, insertId: id });
                      resolve();
                    };
                    
                    request.onerror = (event) => {
                      console.error('Error storing data in IndexedDB:', event.target.error);
                      errorFn && errorFn(tx, event.target.error);
                      reject(event.target.error);
                    };
                  });
                } 
                else if (operation === 'select') {
                  // SELECT operation
                  const id = params[0];
                  const transaction = idbInstance.transaction([tableName], 'readonly');
                  const objectStore = transaction.objectStore(tableName);
                  
                  return new Promise((resolve, reject) => {
                    const request = objectStore.get(id);
                    
                    request.onsuccess = (event) => {
                      const data = event.target.result;
                      
                      const result = {
                        rows: {
                          _array: data ? [data] : [],
                          item: (idx) => (data && idx === 0) ? data : null,
                          length: data ? 1 : 0
                        }
                      };
                      
                      successFn && successFn(tx, result);
                      resolve();
                    };
                    
                    request.onerror = (event) => {
                      console.error('Error retrieving data from IndexedDB:', event.target.error);
                      errorFn && errorFn(tx, event.target.error);
                      reject(event.target.error);
                    };
                  });
                } 
                else {
                  // Unsupported operation
                  errorFn && errorFn(tx, new Error(`Unsupported operation in query: ${query}`));
                }
              } catch (error) {
                console.error('Error in executeSql:', error);
                errorFn && errorFn(tx, error);
              }
            }
          };
          
          await callback(tx);
          successCallback && successCallback();
        } catch (error) {
          console.error('Transaction error:', error);
          errorCallback && errorCallback(error);
        }
      }
    };
  };